import React, { useEffect, useMemo, useState, useCallback } from 'react';
import '../css/FullSuiteModal.css';
import '../css/Forms.css'; // re-use po-* progress + footer styles

export default function FSFModal({ isOpen, onClose }) {
  const STORAGE_KEY = 'FSF_FORM_DRAFT_v1';

  const defaultForm = useMemo(() => ({
    engagementModel: '',
    // Section 1: New fields
    industryProjectType: 'Game Development',
    industryProjectTypeOther: '',
    reportingLine: 'Client PM',
    reportingLineOther: '',
    engagementDuration: '6–12 months',
    // Section 2
    numberOfTalents: '',
    rolesNeeded: [],
    otherRole: '',
    startDate: '',
    duration: '',
    seniorityLevel: 'Mid-Level',
    workArrangement: 'Remote',
    shiftRequirement: 'Day',
    languagesRequired: [],
    languagesOther: '',
    // Section 3
    modules: {
      behavioral: false,
      skills: false,
      structured: false,
      custom: false,
    },
    top3MustHaveSkills: '',
    niceToHaveSkills: '',
    portfolioRequirement: 'Yes',
    certificationRequirement: 'None',
    certificationOther: '',
    // Section 4
    setupLevel: '',
    addOns: {
      watchlist: false,
      social: false,
      credit: false,
      psych: false,
      hiv: false,
    },
    clientToolsAccess: 'No',
    clientToolsAccessDetails: '',
    hardwareRequirements: '',
    securityComplianceNeeds: [],
    securityComplianceOther: '',
    // Section 5
    deploymentMode: '',
    nestingDuration: '2 weeks',
    supervisionLevel: 'Moderate',
    performanceMonitoringFrequency: 'Weekly',
    goLiveReadinessConfirmation: 'Client-led',
    knowledgeTransferMethod: 'Documentation',
    knowledgeTransferOther: '',
    exitInterviewRequired: 'No',
    // Section 6: Budget & Notes
    budgetRange: '',
    maxBudgetCap: '',
    paymentPreference: 'Monthly',
    specialInstructions: '',
    top3MustHaveTeamGoals: '',
    successMetricsKPIs: '',
    pointOfContact: {
      fullName: '',
      email: '',
      phoneWhatsapp: '',
    },
    // Section 7
    confirm: false,
  }), []);

  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0); // 0..6

  // Steps meta (for progress)
  const STEPS = useMemo(() => ([
    { key: 'engagement', title: 'Engagement Model' },
    { key: 'team',       title: 'Team / Talent Requirements' },
    { key: 'modules',    title: 'Screening & Assessments' },
    { key: 'setup',      title: 'Work Enablement & Setup' },
    { key: 'deploy',     title: 'Deployment Preferences' },
    { key: 'notes',      title: 'Notes' },
    { key: 'confirm',    title: 'Confirmation' },
  ]), []);
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // Category catalog for Section 2 (chip selector)
  const ROLE_CATALOG = useMemo(() => ({
    Creative: [
      { value: '3d-artist',      label: '3D Artist (Generalist)' },
      { value: 'uiux-designer',  label: 'UI/UX Designer' },
    ],
    Technical: [
      { value: 'game-programmer', label: 'Game Programmer' },
    ],
    Management: [
      { value: 'project-manager', label: 'Project Manager' },
    ],
    Administrative: [
      { value: 'qa-tester', label: 'QA / Game Tester' },
    ],
  }), []);

  // Load draft whenever modal opens
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setForm({ ...defaultForm, ...parsed });
        setStep(Number.isFinite(+parsed._step) ? +parsed._step : 0);
      } else {
        setForm(defaultForm);
        setStep(0);
      }
      setErrors({});
    } catch {
      setForm(defaultForm);
      setStep(0);
      setErrors({});
    }
  }, [isOpen, defaultForm]);

  // Helpers to update form state
  const updateField  = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const updateNested = (path, value) => {
    const [root, sub] = path.split('.');
    setForm(f => ({ ...f, [root]: { ...f[root], [sub]: value } }));
  };
  const toggleRole = (val) => {
    setForm(f => {
      const set = new Set(f.rolesNeeded);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...f, rolesNeeded: Array.from(set) };
    });
  };

  const toggleMultiSelect = (currentArray, setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const Check = ({ label, checked, onChange }) => (
    <label className="fsf-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );

  // Save/Clear Draft
  const snapshot = () => ({ ...form, _step: step });
  const saveDraft = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot())); } catch {}
    alert('Draft saved ✨');
  };
  const clearDraft = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setForm(defaultForm);
    setErrors({});
    setStep(0);
    alert('Draft cleared.');
  };

  // Step validation
  const validateStep = useCallback((s) => {
    const e = {};
    if (s === 0) {
      if (!form.engagementModel) e.engagementModel = 'Please select an engagement model.';
      if (!form.industryProjectType) e.industryProjectType = 'Please select Industry/Project Type.';
      if (!form.reportingLine) e.reportingLine = 'Please select Reporting Line.';
      if (!form.engagementDuration) e.engagementDuration = 'Please select Engagement Duration.';
      if (form.industryProjectType === 'Other' && !form.industryProjectTypeOther.trim()) e.industryProjectTypeOther = 'Please specify industry/project type.';
      if (form.reportingLine === 'Other' && !form.reportingLineOther.trim()) e.reportingLineOther = 'Please specify reporting line.';
    }
    if (s === 1) {
      if (!form.numberOfTalents || Number(form.numberOfTalents) <= 0) e.numberOfTalents = 'Enter a valid number.';
      if ((form.rolesNeeded || []).length === 0 && !form.otherRole.trim()) e.rolesNeeded = 'Select at least one role or specify Other.';
      if (!form.startDate) e.startDate = 'Pick a target start date.';
      if (!form.duration) e.duration = 'Select expected contract duration.';
      if (!form.seniorityLevel) e.seniorityLevel = 'Please select Seniority Level.';
      if (!form.workArrangement) e.workArrangement = 'Please select Work Arrangement.';
      if (!form.shiftRequirement) e.shiftRequirement = 'Please select Shift Requirement.';
      if (form.languagesRequired.includes('Other') && !form.languagesOther.trim()) e.languagesOther = 'Please specify other languages.';
    }
    if (s === 2) {
      if (!form.top3MustHaveSkills.trim()) e.top3MustHaveSkills = 'Please enter Top 3 Must-Have Skills.';
      if (!form.portfolioRequirement) e.portfolioRequirement = 'Please select Portfolio Requirement.';
      if (!form.certificationRequirement) e.certificationRequirement = 'Please select Certification Requirement.';
      if (form.certificationRequirement === 'Other' && !form.certificationOther.trim()) e.certificationOther = 'Please specify certification requirement.';
    }
    if (s === 3) {
      if (!form.setupLevel) e.setupLevel = 'Pick a setup level.';
      if (!form.clientToolsAccess) e.clientToolsAccess = 'Please select Client Tools Access.';
      if (form.clientToolsAccess === 'Yes' && !form.clientToolsAccessDetails.trim()) e.clientToolsAccessDetails = 'Please specify client tools access details.';
      if (form.securityComplianceNeeds.includes('Other') && !form.securityComplianceOther.trim()) e.securityComplianceOther = 'Please specify other security/compliance needs.';
    }
    if (s === 4) {
      if (!form.deploymentMode) e.deploymentMode = 'Select a deployment mode.';
      if (!form.nestingDuration) e.nestingDuration = 'Please select Nesting Duration.';
      if (!form.supervisionLevel) e.supervisionLevel = 'Please select Supervision Level.';
      if (!form.performanceMonitoringFrequency) e.performanceMonitoringFrequency = 'Please select Performance Monitoring Frequency.';
      if (!form.goLiveReadinessConfirmation) e.goLiveReadinessConfirmation = 'Please select Go-Live Readiness Confirmation.';
      if (!form.knowledgeTransferMethod) e.knowledgeTransferMethod = 'Please select Knowledge Transfer Method.';
      if (!form.exitInterviewRequired) e.exitInterviewRequired = 'Please select Exit Interview requirement.';
      if (form.knowledgeTransferMethod === 'Other' && !form.knowledgeTransferOther.trim()) e.knowledgeTransferOther = 'Please specify knowledge transfer method.';
    }
    if (s === 5) {
      if (!form.budgetRange.trim()) e.budgetRange = 'Please enter Budget Range.';
      if (!form.paymentPreference) e.paymentPreference = 'Please select Payment Preference.';
      if (!form.top3MustHaveTeamGoals.trim()) e.top3MustHaveTeamGoals = 'Please enter Top 3 Must-Have Team Goals.';
      if (!form.successMetricsKPIs.trim()) e.successMetricsKPIs = 'Please enter Success Metrics/KPIs.';
      if (!form.pointOfContact.fullName.trim()) e.pointOfContactFullName = 'Please enter Full Name.';
      if (!form.pointOfContact.email.trim()) e.pointOfContactEmail = 'Please enter Email Address.';
      if (!form.pointOfContact.phoneWhatsapp.trim()) e.pointOfContactPhone = 'Please enter Phone/WhatsApp.';
    }
    if (s === 6) {
      if (!form.confirm) e.confirm = 'You must confirm to proceed.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form]);

  const goNext = () => { if (validateStep(step)) setStep((x) => Math.min(totalSteps - 1, x + 1)); };
  const goBack = () => setStep((x) => Math.max(0, x - 1));

  // Prevent Enter from submitting before the last step
  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && step < totalSteps - 1) {
      e.preventDefault();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (step !== totalSteps - 1) return; // only submit on the final step
    if (!validateStep(step)) return;

    // Final payload
    const payload = { ...form };
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    console.log('FSF SUBMIT:', payload);
    alert('Full Suite request submitted ✔️\n(See console for payload)');
    onClose?.();
  };

  return (
    <div
      className="fsf-overlay"
      aria-modal="true"
      role="dialog"
      aria-label="Full Suite Form"
      aria-hidden={!isOpen}
      style={{ display: isOpen ? 'grid' : 'none' }}
      onClick={onClose}
    >
      <div className="fsf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fsf-header">
          <h2 className="fsf-title">Full Suite Form (FSF)</h2>
          <button className="fsf-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Top progress bar (reusing po-* styles) */}
        <div className="po-progress">
          <div className="po-progress-top"><div className="po-progress-num">{progressPct}%</div></div>
          <div className="po-progress-bar"><div className="po-progress-fill" style={{ width: `${progressPct}%` }} /></div>
        </div>

        <form className="fsf-body" onSubmit={onSubmit} onKeyDown={preventEnterSubmit}>
          {/* SECTION 1: Engagement Model */}
          {step === 0 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 1: Engagement Model</h3>
              
              <div className="fsf-grid-1">
                {[
                  { id: 'eor_std', value: 'EOR Standard', label: 'EOR Standard — Full suite under our Employee-of-Record structure' },
                  { id: 'eor_prem', value: 'EOR Premium', label: 'EOR Premium — Customized onboarding, deployment, and equipment per client specifications' },
                  { id: 'non_eor', value: 'Non-EOR', label: 'Non-EOR (Onboarding Only) — For direct hires with deployment support' },
                  { id: 'white_label', value: 'White Label', label: 'White Label — Entire process under your brand identity' },
                ].map(opt => (
                  <label key={opt.id} className="fsf-radio">
                    <input
                      type="radio"
                      name="engagementModel"
                      value={opt.value}
                      checked={form.engagementModel === opt.value}
                      onChange={(e) => updateField('engagementModel', e.target.value)}
                      required
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {errors.engagementModel && <div className="fsf-error">{errors.engagementModel}</div>}

              <div className="fsf-section-title">Engagement Details</div>
              <div className="fsf-grid-3">
                <div className="fsf-field">
                  <label className="fsf-label">Industry / Project Type <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.industryProjectType}
                    onChange={(e) => updateField('industryProjectType', e.target.value)}
                    required
                  >
                    <option value="Game Development">Game Development</option>
                    <option value="Animation">Animation</option>
                    <option value="VFX">VFX</option>
                    <option value="Tech">Tech</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.industryProjectType && <div className="fsf-error">{errors.industryProjectType}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Reporting Line <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.reportingLine}
                    onChange={(e) => updateField('reportingLine', e.target.value)}
                    required
                  >
                    <option value="Client PM">Client PM</option>
                    <option value="Exodia Supervisor">Exodia Supervisor</option>
                    <option value="Joint Supervision">Joint Supervision</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reportingLine && <div className="fsf-error">{errors.reportingLine}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Engagement Duration <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.engagementDuration}
                    onChange={(e) => updateField('engagementDuration', e.target.value)}
                    required
                  >
                    <option value="<3 months">&lt;3 months</option>
                    <option value="3–6 months">3–6 months</option>
                    <option value="6–12 months">6–12 months</option>
                    <option value="1 year+">1 year+</option>
                    <option value="Indefinite">Indefinite</option>
                  </select>
                  {errors.engagementDuration && <div className="fsf-error">{errors.engagementDuration}</div>}
                </div>
              </div>

              {form.industryProjectType === 'Other' && (
                <div className="fsf-field">
                  <label className="fsf-label">Please specify industry/project type</label>
                  <input
                    className="fsf-input"
                    placeholder="Enter industry/project type..."
                    value={form.industryProjectTypeOther}
                    onChange={(e) => updateField('industryProjectTypeOther', e.target.value)}
                    required
                  />
                  {errors.industryProjectTypeOther && <div className="fsf-error">{errors.industryProjectTypeOther}</div>}
                </div>
              )}

              {form.reportingLine === 'Other' && (
                <div className="fsf-field">
                  <label className="fsf-label">Please specify reporting line</label>
                  <input
                    className="fsf-input"
                    placeholder="Enter reporting line details..."
                    value={form.reportingLineOther}
                    onChange={(e) => updateField('reportingLineOther', e.target.value)}
                    required
                  />
                  {errors.reportingLineOther && <div className="fsf-error">{errors.reportingLineOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* SECTION 2: Team / Talent Requirements */}
          {step === 1 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 2: Team / Talent Requirements</h3>

              <div className="fsf-grid-2">
                <div className="fsf-field">
                  <label className="fsf-label">Number of Talents Required</label>
                  <input
                    className="fsf-input"
                    type="number"
                    min="1"
                    placeholder="e.g., 5"
                    value={form.numberOfTalents}
                    onChange={(e) => updateField('numberOfTalents', e.target.value)}
                    required
                  />
                  {errors.numberOfTalents && <div className="fsf-error">{errors.numberOfTalents}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Target Start Date</label>
                  <input
                    className="fsf-input"
                    type="date"
                    value={form.startDate}
                    onChange={(e) => updateField('startDate', e.target.value)}
                    required
                  />
                  {errors.startDate && <div className="fsf-error">{errors.startDate}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Expected Contract Duration</label>
                  <select
                    className="fsf-select"
                    value={form.duration}
                    onChange={(e) => updateField('duration', e.target.value)}
                    required
                  >
                    <option value="">Select duration…</option>
                    <option value="<3 months">&lt;3 months</option>
                    <option value="3–6 months">3–6 months</option>
                    <option value="6–12 months">6–12 months</option>
                    <option value="1 year+">1 year+</option>
                  </select>
                  {errors.duration && <div className="fsf-error">{errors.duration}</div>}
                </div>
              </div>

              {/* Category-based multi-select chips */}
              <div className="fsf-role-section">
                <div className="fsf-label" style={{ marginBottom: 6 }}>Roles Needed (multi-select)</div>

                {Object.entries(ROLE_CATALOG).map(([category, roles]) => (
                  <div className="fsf-role-category" key={category}>
                    <div className="fsf-cat-title">{category}</div>
                    <div className="fsf-role-grid">
                      {roles.map((r) => {
                        const checked = form.rolesNeeded.includes(r.value);
                        return (
                          <label key={r.value} className={`fsf-role-chip ${checked ? 'selected' : ''}`}>
                            <input
                              type="checkbox"
                              className="fsf-sr-only"
                              checked={checked}
                              onChange={() => toggleRole(r.value)}
                            />
                            <span className="fsf-chip-text">{r.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="fsf-help">Tip: select as many roles as you need.</div>
              </div>

              <div className="fsf-field fsf-field--full" style={{ marginTop: 8 }}>
                <label className="fsf-label">Other Role (specify)</label>
                <input
                  className="fsf-input"
                  type="text"
                  placeholder="e.g., Technical Artist"
                  value={form.otherRole}
                  onChange={(e) => updateField('otherRole', e.target.value)}
                />
                {errors.rolesNeeded && <div className="fsf-error">{errors.rolesNeeded}</div>}
              </div>

              <div className="fsf-section-title">Talent Profile Requirements</div>
              <div className="fsf-grid-3">
                <div className="fsf-field">
                  <label className="fsf-label">Seniority Level Needed <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.seniorityLevel}
                    onChange={(e) => updateField('seniorityLevel', e.target.value)}
                    required
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid-Level">Mid-Level</option>
                    <option value="Senior">Senior</option>
                  </select>
                  {errors.seniorityLevel && <div className="fsf-error">{errors.seniorityLevel}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Work Arrangement <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.workArrangement}
                    onChange={(e) => updateField('workArrangement', e.target.value)}
                    required
                  >
                    <option value="Onsite">Onsite</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Remote">Remote</option>
                  </select>
                  {errors.workArrangement && <div className="fsf-error">{errors.workArrangement}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Shift Requirement <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.shiftRequirement}
                    onChange={(e) => updateField('shiftRequirement', e.target.value)}
                    required
                  >
                    <option value="Day">Day</option>
                    <option value="Night">Night</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                  {errors.shiftRequirement && <div className="fsf-error">{errors.shiftRequirement}</div>}
                </div>
              </div>

              <div className="fsf-section-title">Languages Required</div>
              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Languages Required</label>
                <div className="fsf-checkbox-grid">
                  {['English', 'Japanese', 'Korean', 'Mandarin'].map((lang) => (
                    <Check
                      key={lang}
                      label={lang}
                      checked={form.languagesRequired.includes(lang)}
                      onChange={(checked) => {
                        if (checked) {
                          setForm(f => ({ ...f, languagesRequired: [...f.languagesRequired, lang] }));
                        } else {
                          setForm(f => ({ ...f, languagesRequired: f.languagesRequired.filter(item => item !== lang) }));
                        }
                      }}
                    />
                  ))}
                  <Check
                    label="Other"
                    checked={form.languagesRequired.includes('Other')}
                    onChange={(checked) => {
                      if (checked) {
                        setForm(f => ({ ...f, languagesRequired: [...f.languagesRequired, 'Other'] }));
                      } else {
                        setForm(f => ({ ...f, languagesRequired: f.languagesRequired.filter(item => item !== 'Other') }));
                      }
                    }}
                  />
                </div>

                {form.languagesRequired.includes('Other') && (
                  <div className="fsf-field">
                    <label className="fsf-label">Please specify other languages</label>
                    <input
                      className="fsf-input"
                      placeholder="Enter other languages..."
                      value={form.languagesOther}
                      onChange={(e) => updateField('languagesOther', e.target.value)}
                      required
                    />
                    {errors.languagesOther && <div className="fsf-error">{errors.languagesOther}</div>}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* SECTION 3: Screening & Assessment Preferences */}
          {step === 2 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 3: Screening & Assessment Preferences</h3>
              
              <div className="fsf-section-title">Assessment Modules</div>
              <div className="fsf-checkbox-grid">
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.modules.behavioral}
                    onChange={(e) => updateNested('modules.behavioral', e.target.checked)}
                  />
                  <span>Behavioral & Personality Assessments</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.modules.skills}
                    onChange={(e) => updateNested('modules.skills', e.target.checked)}
                  />
                  <span>Skills & Aptitude Testing</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.modules.structured}
                    onChange={(e) => updateNested('modules.structured', e.target.checked)}
                  />
                  <span>Structured Interview Rounds</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.modules.custom}
                    onChange={(e) => updateNested('modules.custom', e.target.checked)}
                  />
                  <span>Custom Module (Contact Sales)</span>
                </label>
              </div>

              <div className="fsf-section-title">Skills Requirements</div>
              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Top 3 Must-Have Skills <span className="req">*</span></label>
                <textarea
                  className="fsf-textarea"
                  rows={3}
                  placeholder="List the top 3 must-have skills for this role..."
                  value={form.top3MustHaveSkills}
                  onChange={(e) => updateField('top3MustHaveSkills', e.target.value)}
                  required
                />
                {errors.top3MustHaveSkills && <div className="fsf-error">{errors.top3MustHaveSkills}</div>}
              </div>

              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Nice-to-Have Skills</label>
                <textarea
                  className="fsf-textarea"
                  rows={3}
                  placeholder="List any nice-to-have skills (optional)..."
                  value={form.niceToHaveSkills}
                  onChange={(e) => updateField('niceToHaveSkills', e.target.value)}
                />
              </div>

              <div className="fsf-section-title">Requirements</div>
              <div className="fsf-grid-2">
                <div className="fsf-field">
                  <label className="fsf-label">Portfolio/Work Sample Requirement <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.portfolioRequirement}
                    onChange={(e) => updateField('portfolioRequirement', e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.portfolioRequirement && <div className="fsf-error">{errors.portfolioRequirement}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Certification Requirement <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.certificationRequirement}
                    onChange={(e) => updateField('certificationRequirement', e.target.value)}
                    required
                  >
                    <option value="Unity Certified">Unity Certified</option>
                    <option value="Scrum Master">Scrum Master</option>
                    <option value="PMP">PMP</option>
                    <option value="None">None</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.certificationRequirement && <div className="fsf-error">{errors.certificationRequirement}</div>}
                </div>
              </div>

              {form.certificationRequirement === 'Other' && (
                <div className="fsf-field">
                  <label className="fsf-label">Please specify certification requirement</label>
                  <input
                    className="fsf-input"
                    placeholder="Enter certification requirement..."
                    value={form.certificationOther}
                    onChange={(e) => updateField('certificationOther', e.target.value)}
                    required
                  />
                  {errors.certificationOther && <div className="fsf-error">{errors.certificationOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* SECTION 4: Work Enablement & Setup */}
          {step === 3 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 4: Work Enablement & Setup</h3>
              <div className="fsf-grid-1">
                <label className="fsf-radio">
                  <input
                    type="radio"
                    name="setupLevel"
                    value="standard"
                    checked={form.setupLevel === 'standard'}
                    onChange={(e) => updateField('setupLevel', e.target.value)}
                    required
                  />
                  <span>Standard Workstation & IT Access — enterprise-grade standard setup</span>
                </label>
                <label className="fsf-radio">
                  <input
                    type="radio"
                    name="setupLevel"
                    value="premium"
                    checked={form.setupLevel === 'premium'}
                    onChange={(e) => updateField('setupLevel', e.target.value)}
                    required
                  />
                  <span>Premium Workstation & IT Access — fully customized, high-performance setup</span>
                </label>
                {errors.setupLevel && <div className="fsf-error">{errors.setupLevel}</div>}
              </div>

              <div className="fsf-section-title">Client Tools & Hardware</div>
              <div className="fsf-grid-2">
                <div className="fsf-field">
                  <label className="fsf-label">Client Tools Access Provided <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.clientToolsAccess}
                    onChange={(e) => updateField('clientToolsAccess', e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.clientToolsAccess && <div className="fsf-error">{errors.clientToolsAccess}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Hardware/Device Requirements</label>
                  <input
                    className="fsf-input"
                    placeholder="e.g., VR-ready PC, iOS dev kit"
                    value={form.hardwareRequirements}
                    onChange={(e) => updateField('hardwareRequirements', e.target.value)}
                  />
                </div>
              </div>

              {form.clientToolsAccess === 'Yes' && (
                <div className="fsf-field fsf-field--full">
                  <label className="fsf-label">Client Tools Access Details <span className="req">*</span></label>
                  <textarea
                    className="fsf-textarea"
                    rows={3}
                    placeholder="Specify which client tools will be provided..."
                    value={form.clientToolsAccessDetails}
                    onChange={(e) => updateField('clientToolsAccessDetails', e.target.value)}
                    required
                  />
                  {errors.clientToolsAccessDetails && <div className="fsf-error">{errors.clientToolsAccessDetails}</div>}
                </div>
              )}

              <div className="fsf-section-title">Security/Compliance Needs</div>
              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Security/Compliance Needs</label>
                <div className="fsf-checkbox-grid">
                  {['NDA signing', 'IP restrictions', 'Limited access accounts'].map((need) => (
                    <Check
                      key={need}
                      label={need}
                      checked={form.securityComplianceNeeds.includes(need)}
                      onChange={(checked) => {
                        if (checked) {
                          setForm(f => ({ ...f, securityComplianceNeeds: [...f.securityComplianceNeeds, need] }));
                        } else {
                          setForm(f => ({ ...f, securityComplianceNeeds: f.securityComplianceNeeds.filter(item => item !== need) }));
                        }
                      }}
                    />
                  ))}
                  <Check
                    label="Other"
                    checked={form.securityComplianceNeeds.includes('Other')}
                    onChange={(checked) => {
                      if (checked) {
                        setForm(f => ({ ...f, securityComplianceNeeds: [...f.securityComplianceNeeds, 'Other'] }));
                      } else {
                        setForm(f => ({ ...f, securityComplianceNeeds: f.securityComplianceNeeds.filter(item => item !== 'Other') }));
                      }
                    }}
                  />
                </div>

                {form.securityComplianceNeeds.includes('Other') && (
                  <div className="fsf-field">
                    <label className="fsf-label">Please specify other security/compliance needs</label>
                    <input
                      className="fsf-input"
                      placeholder="Enter other security/compliance needs..."
                      value={form.securityComplianceOther}
                      onChange={(e) => updateField('securityComplianceOther', e.target.value)}
                      required
                    />
                    {errors.securityComplianceOther && <div className="fsf-error">{errors.securityComplianceOther}</div>}
                  </div>
                )}
              </div>

              <div className="fsf-subtitle">Optional Add-ons</div>
              <div className="fsf-checkbox-grid">
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.addOns.watchlist}
                    onChange={(e) => updateNested('addOns.watchlist', e.target.checked)}
                  />
                  <span>Anti-Terror / Watchlist Screening</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.addOns.social}
                    onChange={(e) => updateNested('addOns.social', e.target.checked)}
                  />
                  <span>Social Media Screening</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.addOns.credit}
                    onChange={(e) => updateNested('addOns.credit', e.target.checked)}
                  />
                  <span>Credit History Check</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.addOns.psych}
                    onChange={(e) => updateNested('addOns.psych', e.target.checked)}
                  />
                  <span>Psychological Exam</span>
                </label>
                <label className="fsf-check">
                  <input
                    type="checkbox"
                    checked={form.addOns.hiv}
                    onChange={(e) => updateNested('addOns.hiv', e.target.checked)}
                  />
                  <span>HIV & Hepatitis B Screening</span>
                </label>
              </div>
            </section>
          )}

          {/* SECTION 5: Deployment & Offboarding Preferences */}
          {step === 4 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 5: Deployment & Offboarding Preferences</h3>
              
              <div className="fsf-section-title">Deployment Settings</div>
              <div className="fsf-grid-2">
                <div className="fsf-field">
                  <label className="fsf-label">Deployment Mode <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.deploymentMode}
                    onChange={(e) => updateField('deploymentMode', e.target.value)}
                    required
                  >
                    <option value="">Select deployment mode…</option>
                    <option value="standard-30">Standard 30-day deployment</option>
                    <option value="fast-14">Fast-Track 14-day deployment (additional fee per talent)</option>
                  </select>
                  {errors.deploymentMode && <div className="fsf-error">{errors.deploymentMode}</div>}
                </div>
                <div className="fsf-field">
                  <label className="fsf-label">Offboarding Management</label>
                  <input className="fsf-input" value="Handled by Exodia (auto-confirmed)" readOnly />
                </div>
              </div>

              <div className="fsf-section-title">Nesting & Go-Live</div>
              <div className="fsf-grid-3">
                <div className="fsf-field">
                  <label className="fsf-label">Nesting Duration <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.nestingDuration}
                    onChange={(e) => updateField('nestingDuration', e.target.value)}
                    required
                  >
                    <option value="1 week">1 week</option>
                    <option value="2 weeks">2 weeks</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {errors.nestingDuration && <div className="fsf-error">{errors.nestingDuration}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Supervision Level <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.supervisionLevel}
                    onChange={(e) => updateField('supervisionLevel', e.target.value)}
                    required
                  >
                    <option value="Light">Light</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Intensive">Intensive</option>
                  </select>
                  {errors.supervisionLevel && <div className="fsf-error">{errors.supervisionLevel}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Performance Monitoring Frequency <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.performanceMonitoringFrequency}
                    onChange={(e) => updateField('performanceMonitoringFrequency', e.target.value)}
                    required
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-weekly">Bi-weekly</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {errors.performanceMonitoringFrequency && <div className="fsf-error">{errors.performanceMonitoringFrequency}</div>}
                </div>
              </div>

              <div className="fsf-section-title">Go-Live & Knowledge Transfer</div>
              <div className="fsf-grid-2">
                <div className="fsf-field">
                  <label className="fsf-label">Go-Live Readiness Confirmation <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.goLiveReadinessConfirmation}
                    onChange={(e) => updateField('goLiveReadinessConfirmation', e.target.value)}
                    required
                  >
                    <option value="Client-led">Client-led</option>
                    <option value="Exodia-led">Exodia-led</option>
                    <option value="Joint">Joint</option>
                  </select>
                  {errors.goLiveReadinessConfirmation && <div className="fsf-error">{errors.goLiveReadinessConfirmation}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Knowledge Transfer Method <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.knowledgeTransferMethod}
                    onChange={(e) => updateField('knowledgeTransferMethod', e.target.value)}
                    required
                  >
                    <option value="Documentation">Documentation</option>
                    <option value="Shadowing">Shadowing</option>
                    <option value="Recorded sessions">Recorded sessions</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.knowledgeTransferMethod && <div className="fsf-error">{errors.knowledgeTransferMethod}</div>}
                </div>
              </div>

              {form.knowledgeTransferMethod === 'Other' && (
                <div className="fsf-field">
                  <label className="fsf-label">Please specify knowledge transfer method</label>
                  <input
                    className="fsf-input"
                    placeholder="Enter knowledge transfer method..."
                    value={form.knowledgeTransferOther}
                    onChange={(e) => updateField('knowledgeTransferOther', e.target.value)}
                    required
                  />
                  {errors.knowledgeTransferOther && <div className="fsf-error">{errors.knowledgeTransferOther}</div>}
                </div>
              )}

              <div className="fsf-section-title">Exit Interview</div>
              <div className="fsf-field">
                <label className="fsf-label">Exit Interview Required? <span className="req">*</span></label>
                <select
                  className="fsf-select"
                  value={form.exitInterviewRequired}
                  onChange={(e) => updateField('exitInterviewRequired', e.target.value)}
                  required
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="Client-led">Client-led</option>
                  <option value="Exodia-led">Exodia-led</option>
                  <option value="Joint">Joint</option>
                </select>
                {errors.exitInterviewRequired && <div className="fsf-error">{errors.exitInterviewRequired}</div>}
              </div>
            </section>
          )}

          {/* SECTION 6: Budget & Notes */}
          {step === 5 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 6: Budget & Notes</h3>
              
              <div className="fsf-section-title">Budget Information</div>
              <div className="fsf-grid-3">
                <div className="fsf-field">
                  <label className="fsf-label">Budget Range (per role or per hire) <span className="req">*</span></label>
                  <input
                    className="fsf-input"
                    placeholder="e.g., $3000-5000"
                    value={form.budgetRange}
                    onChange={(e) => updateField('budgetRange', e.target.value)}
                    required
                  />
                  {errors.budgetRange && <div className="fsf-error">{errors.budgetRange}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Maximum Budget Cap (optional)</label>
                  <input
                    className="fsf-input"
                    type="number"
                    placeholder="e.g., 100000"
                    value={form.maxBudgetCap}
                    onChange={(e) => updateField('maxBudgetCap', e.target.value)}
                  />
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Payment Preference <span className="req">*</span></label>
                  <select
                    className="fsf-select"
                    value={form.paymentPreference}
                    onChange={(e) => updateField('paymentPreference', e.target.value)}
                    required
                  >
                    <option value="Per milestone">Per milestone</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Per talent">Per talent</option>
                  </select>
                  {errors.paymentPreference && <div className="fsf-error">{errors.paymentPreference}</div>}
                </div>
              </div>

              <div className="fsf-section-title">Special Instructions & Goals</div>
              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Special Instructions / Additional Requirements</label>
                <textarea
                  className="fsf-textarea"
                  rows={4}
                  placeholder="Add any special requests or operational notes..."
                  value={form.specialInstructions}
                  onChange={(e) => updateField('specialInstructions', e.target.value)}
                />
              </div>

              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Top 3 Must-Have Team Goals <span className="req">*</span></label>
                <textarea
                  className="fsf-textarea"
                  rows={3}
                  placeholder="e.g., Team must be production-ready within 30 days"
                  value={form.top3MustHaveTeamGoals}
                  onChange={(e) => updateField('top3MustHaveTeamGoals', e.target.value)}
                  required
                />
                {errors.top3MustHaveTeamGoals && <div className="fsf-error">{errors.top3MustHaveTeamGoals}</div>}
              </div>

              <div className="fsf-field fsf-field--full">
                <label className="fsf-label">Success Metrics / KPIs <span className="req">*</span></label>
                <textarea
                  className="fsf-textarea"
                  rows={3}
                  placeholder="We consider Full Suite successful if..."
                  value={form.successMetricsKPIs}
                  onChange={(e) => updateField('successMetricsKPIs', e.target.value)}
                  required
                />
                {errors.successMetricsKPIs && <div className="fsf-error">{errors.successMetricsKPIs}</div>}
              </div>

              <div className="fsf-section-title">Point of Contact for Full Suite</div>
              <div className="fsf-grid-3">
                <div className="fsf-field">
                  <label className="fsf-label">Full Name <span className="req">*</span></label>
                  <input
                    className="fsf-input"
                    placeholder="Enter full name"
                    value={form.pointOfContact.fullName}
                    onChange={(e) => updateNested('pointOfContact.fullName', e.target.value)}
                    required
                  />
                  {errors.pointOfContactFullName && <div className="fsf-error">{errors.pointOfContactFullName}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Email Address <span className="req">*</span></label>
                  <input
                    className="fsf-input"
                    type="email"
                    placeholder="Enter email address"
                    value={form.pointOfContact.email}
                    onChange={(e) => updateNested('pointOfContact.email', e.target.value)}
                    required
                  />
                  {errors.pointOfContactEmail && <div className="fsf-error">{errors.pointOfContactEmail}</div>}
                </div>

                <div className="fsf-field">
                  <label className="fsf-label">Phone/WhatsApp <span className="req">*</span></label>
                  <input
                    className="fsf-input"
                    placeholder="Enter phone/WhatsApp"
                    value={form.pointOfContact.phoneWhatsapp}
                    onChange={(e) => updateNested('pointOfContact.phoneWhatsapp', e.target.value)}
                    required
                  />
                  {errors.pointOfContactPhone && <div className="fsf-error">{errors.pointOfContactPhone}</div>}
                </div>
              </div>
            </section>
          )}

          {/* SECTION 7: Confirmation */}
          {step === 6 && (
            <section className="fsf-card">
              <h3 className="fsf-card-title">Section 7: Confirmation</h3>
              <label className="fsf-check">
                <input
                  type="checkbox"
                  checked={form.confirm}
                  onChange={(e) => updateField('confirm', e.target.checked)}
                  required
                />
                <span>
                  I confirm my request for Full Suite services. Exodia will handle all stages from
                  Pre-Onboarding to Offboarding as per the chosen engagement model.
                </span>
              </label>
              {errors.confirm && <div className="fsf-error">{errors.confirm}</div>}
            </section>
          )}

          {/* Wizard footer (exact block you use elsewhere) */}
          <div className="po-footer po-footer--wizard">
            <div className="po-footer-left">
              <button type="button" className="po-btn po-btn-ghost" onClick={saveDraft}>Save Draft</button>
              <button type="button" className="po-btn po-btn-ghost" onClick={clearDraft}>
                Clear Draft
              </button>
            </div>
            <div className="po-footer-right">
              <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
              {step > 0 && (
                <button type="button" className="po-btn" onClick={goBack}>Back</button>
              )}
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  className="po-btn po-btn-primary"
                  onClick={(e) => { e.preventDefault(); goNext(); }}
                  formNoValidate
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="po-btn po-btn-primary">Start Full Suite</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
